import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, IconButton, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ConfigCard from './ConfigCard';

const LANGS = ['nl', 'en', 'fr'];

/**
 * "Custom links" card. Admins add their own links beyond the required pages.
 * Each link has per-language labels (NL/EN/FR) plus a single URL.
 *
 * value: array of { id, url, labels: { nl, en, fr } }
 * onChange(nextArray) — replaces the full list (parent decides ID strategy).
 */
const CustomLinksCard = ({ value = [], onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const list = Array.isArray(value) ? value : [];

    const update = (idx, patch) => {
        const next = list.map((item, i) => i === idx ? { ...item, ...patch } : item);
        onChange && onChange(next);
    };
    const updateLabel = (idx, lang, val) => {
        update(idx, { labels: { ...(list[idx]?.labels || {}), [lang]: val } });
    };
    const addLink = () => {
        onChange && onChange([
            ...list,
            { id: `cl_${Date.now()}`, url: '', labels: { nl: '', en: '', fr: '' } },
        ]);
    };
    const removeLink = (idx) => {
        onChange && onChange(list.filter((_, i) => i !== idx));
    };

    return (
        <ConfigCard
            title={tr('cfg_footer_custom_links_title', 'Custom links')}
            description={tr('cfg_footer_custom_links_desc', 'Add extra links the storefront will render in the footer.')}
            theme={theme}
        >
            <Stack spacing={2}>
                {list.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        {tr('cfg_footer_custom_links_empty', 'No custom links yet. Click "Add link" below to create one.')}
                    </Typography>
                )}
                {list.map((item, idx) => (
                    <CustomLinkRow
                        key={item.id ?? idx}
                        item={item}
                        onUrlChange={(v) => update(idx, { url: v })}
                        onLabelChange={(lang, v) => updateLabel(idx, lang, v)}
                        onRemove={() => removeLink(idx)}
                        tr={tr}
                    />
                ))}
                <Box>
                    <Button size="small" startIcon={<AddIcon />} onClick={addLink} variant="outlined">
                        {tr('cfg_footer_custom_links_add', 'Add link')}
                    </Button>
                </Box>
            </Stack>
        </ConfigCard>
    );
};

const CustomLinkRow = ({ item, onUrlChange, onLabelChange, onRemove, tr }) => {
    const [langTab, setLangTab] = React.useState(0);
    const lang = LANGS[langTab];
    const labelValue = item.labels?.[lang] ?? '';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                    size="small"
                    label={tr('cfg_footer_custom_links_url', 'Link URL')}
                    value={item.url || ''}
                    onChange={(e) => onUrlChange(e.target.value)}
                    fullWidth
                    placeholder="https://example.com/page"
                />
                <IconButton size="small" onClick={onRemove} title={tr('cfg_footer_custom_links_remove', 'Remove link')}>
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box>
                <Tabs
                    value={langTab}
                    onChange={(_e, v) => setLangTab(v)}
                    variant="standard"
                    sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, px: 1.5, py: 0.5, fontSize: '0.75rem', textTransform: 'none' } }}
                >
                    {LANGS.map((l) => <Tab key={l} label={l.toUpperCase()} />)}
                </Tabs>
                <TextField
                    size="small"
                    fullWidth
                    label={tr('cfg_footer_custom_links_label', 'Label')}
                    value={labelValue}
                    onChange={(e) => onLabelChange(lang, e.target.value)}
                    sx={{ mt: 1 }}
                />
            </Box>
        </Box>
    );
};

CustomLinksCard.propTypes = {
    value:    PropTypes.array,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default CustomLinksCard;
